/**
 * bubu-capa.js — capas modales del tema (menú lateral, hojas, cesta).
 *
 * <bubu-capa id="x" hidden> … </bubu-capa>
 *   [data-bubu-abrir="x"]  → abre
 *   [data-bubu-cerrar]     → cierra (X de 44px o fondo)
 *   Escape                 → cierra
 *   Toque en el fondo      → cierra (el fondo lleva data-bubu-cerrar)
 *
 * <bubu-hoja> hereda todo lo anterior y añade el arrastre hacia abajo
 * (README «Cierre de hojas y modales»):
 *   - arranca desde las zonas que NO hacen scroll: tirador, cabecera, barra de hitos,
 *     cinta de regalo y el botón de pagar;
 *   - y TAMBIÉN desde la lista, cuando ya está arriba del todo y el dedo va hacia
 *     abajo: es el gesto de cualquier hoja de iOS y es lo que hace que la hoja
 *     parezca que sigue al dedo en vez de tener una tira mágica para cerrarla;
 *   - NO arranca si el toque nace en input/select/textarea
 *   - el puntero se captura SOLO pasados 6px de recorrido
 *   - cierra si recorrido > 96px o velocidad > 0,5 px/ms; si no, vuelve con
 *     transform var(--bubu-t-vuelve) var(--bubu-ease-vuelve)
 */

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
const abiertas = [];

function bloqueaScroll(si) {
  const html = document.documentElement;
  if (si) {
    if (!html.dataset.bubuScrollY) {
      html.dataset.bubuScrollY = String(window.scrollY);
      const sb = window.innerWidth - html.clientWidth;
      document.body.style.overflow = 'hidden';
      if (sb > 0) document.body.style.paddingRight = sb + 'px';
    }
  } else if (abiertas.length === 0) {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    delete html.dataset.bubuScrollY;
  }
}

export class BubuCapa extends HTMLElement {
  constructor() {
    super();
    this._onKey = this._onKey.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('keydown', this._onKey);
  }

  get abierta() {
    return !this.hidden;
  }

  abrir(origen) {
    /* REABRIR MIENTRAS SE ESTÁ CERRANDO NO PUEDE PERDER EL TOQUE.
       Aquí estaba el «abro y cierro rápido y se encalla». El cierre dura su animación
       (~220 ms, con plazo de seguridad a 320) y durante todo ese rato la capa sigue SIN
       `hidden`, así que `abierta` decía true y este método se iba de vacío: el toque se
       perdía. Y lo peor venía después: el cierre pendiente seguía su curso y, un instante
       más tarde, ponía `hidden` igualmente. O sea que al cliente le desaparecía el
       carrito justo después de haber pulsado para abrirlo.
       Ahora, si hay un cierre en marcha, se CANCELA y la capa se queda abierta, que es lo
       que ha pedido el dedo. Medido antes del arreglo: pulsar a los 60 ms del cierre
       dejaba `hidden = true` a los 700 ms. */
    if (this.classList.contains('is-cerrando')) {
      this._cancelaCierre();
      return;
    }
    if (this.abierta) return;
    this._origen = origen || document.activeElement;
    this.hidden = false;
    this.classList.remove('is-cerrando');
    abiertas.push(this);
    bloqueaScroll(true);
    document.addEventListener('keydown', this._onKey);
    document.querySelectorAll(`[data-bubu-abrir="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const panel = this.querySelector('[role="dialog"]') || this;
    const primero = this.querySelector('[data-bubu-foco]') || panel.querySelector(FOCUSABLE);
    requestAnimationFrame(() => (primero || panel).focus({ preventScroll: true }));
    this.dispatchEvent(new CustomEvent('bubu:abrir', { bubbles: true }));
  }

  cerrar() {
    if (!this.abierta || this.classList.contains('is-cerrando')) return;
    this.classList.add('is-cerrando');
    const fin = () => {
      this.classList.remove('is-cerrando');
      this.hidden = true;
      const i = abiertas.indexOf(this);
      if (i >= 0) abiertas.splice(i, 1);
      bloqueaScroll(false);
      document.removeEventListener('keydown', this._onKey);
      document.querySelectorAll(`[data-bubu-abrir="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this._limpiaArrastre && this._limpiaArrastre();
      if (this._origen && this._origen.focus) this._origen.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('bubu:cerrar', { bubbles: true }));
    };
    const panel = this.querySelector('[role="dialog"]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (panel && !reduce) {
      let hecho = false;
      let reloj = null;
      const once = () => {
        if (hecho) return;
        hecho = true;
        this._cierre = null;
        clearTimeout(reloj);
        fin();
      };
      // El mando a distancia del cierre, para que abrir() pueda pararlo a medias.
      this._cierre = () => {
        hecho = true;
        this._cierre = null;
        clearTimeout(reloj);
      };
      panel.addEventListener('animationend', once, { once: true });
      panel.addEventListener('transitionend', once, { once: true });
      reloj = setTimeout(once, 320);
    } else fin();
  }

  /** Deshace un cierre a medio camino: la capa se queda abierta y entera. */
  _cancelaCierre() {
    if (this._cierre) this._cierre();
    this.classList.remove('is-cerrando');
    const panel = this.querySelector('[role="dialog"]');
    if (panel) {
      // El arrastre puede haber dejado el panel desplazado y con su transición puesta.
      panel.style.transition = '';
      panel.style.transform = '';
    }
  }

  _onKey(e) {
    if (e.key === 'Escape' && abiertas[abiertas.length - 1] === this) {
      e.preventDefault();
      this.cerrar();
    } else if (e.key === 'Tab') {
      // Foco encerrado dentro de la capa.
      const focos = Array.from(this.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!focos.length) return;
      const first = focos[0];
      const last = focos[focos.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  _onClick(e) {
    const c = e.target.closest('[data-bubu-cerrar]');
    if (c && this.contains(c)) {
      e.preventDefault();
      this.cerrar();
    }
  }
}

export class BubuHoja extends BubuCapa {
  connectedCallback() {
    super.connectedCallback();
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._sincronizaGesto = this._sincronizaGesto.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this.addEventListener('pointerdown', this._onDown);
    // Cuando el arrastre nace SOBRE la lista (dedo abajo con la lista ya arriba del
    // todo), hay que quitarle el gesto al navegador o se pelean el scroll nativo y el
    // arrastre. preventDefault solo funciona desde una escucha no pasiva. Ver la nota
    // de _onTouchMove: se usa ya durante la duda, porque esperar a tener el arrastre
    // decidido llegaba tarde (iOS ya había disparado la goma y matado el puntero).
    this.addEventListener('touchmove', this._onTouchMove, { passive: false });
    // Red de seguridad: si quedara un touch-action escrito a mano en el cuerpo (lo
    // hacía una versión anterior de este archivo, y era lo que dejaba el carrito sin
    // deslizamiento), se limpia al abrir la hoja y después de cada repintado.
    //
    // ANTES esto escuchaba además 'scroll' en fase de captura: se ejecutaba en CADA
    // fotograma del deslizamiento del cajón —y desde el último pase hacía tres
    // querySelector por fotograma— solo para comprobar un estilo que ya no escribe
    // nadie. Era lastre puro en el gesto más usado de la web. Con abrir + repintado
    // basta: son los dos únicos momentos en que ese estilo podría reaparecer.
    document.addEventListener('bubu:seccion-repintada', this._sincronizaGesto);
    this._sincronizaGesto();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this._onDown);
    this.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('bubu:seccion-repintada', this._sincronizaGesto);
  }

  get _scroller() {
    // El que DE VERDAD se desplaza, no el que lo parece. «Monta tu caja» ha tenido las
    // dos formas —scroll en la rejilla y scroll en el cuerpo entero— y devolver el que
    // no era dejaba el arrastre preguntando por un scrollTop que siempre valía 0.
    // En la hoja de «Monta tu caja» el que se desplaza es la propia .bubu-caja (regla
    // «manda el de fuera» en bubu-caja.css). No estaba en esta lista, así que el
    // arrastre miraba un scrollTop que siempre valía 0 y se quedaba con el gesto de
    // subir la lista: con muchos Bubus dentro, mover el dedo hacia abajo cerraba la hoja.
    const cand = [
      this.querySelector('[data-bubu-scroll]'),
      this.querySelector('.bubu-hoja__cuerpo--caja > [data-bubu-caja]'),
      this.querySelector('.bubu-hoja__cuerpo--caja'),
      this.querySelector('[data-bubu-slots-scroll]'),
    ].filter(Boolean);
    return cand.find((n) => n.scrollHeight > n.clientHeight + 1) || cand[0] || null;
  }

  /**
   * Hojas con zona de arrastre ancha: «Monta tu caja» y el carrito. Además del
   * tirador y la cabecera se arrastran desde el pie del total, aunque ahí haya
   * botones: acertar en una tira de 20 px o rendirse y usar la X no era una opción.
   */
  get _arrastreLibre() {
    return this.classList.contains('bubu-hoja--caja') || this.hasAttribute('data-bubu-arrastre-movil');
  }

  _sincronizaGesto() {
    // El touch-action ya NO se cambia sobre la marcha. Ponerlo en 'none' mientras la
    // lista estaba arriba del todo dejaba la hoja sin poder empezar a deslizarse: el
    // mismo gesto que tenía que bajar la lista se lo quedaba el arrastre. Ahora el
    // reparto es fijo y por zonas, en bubu-caja.css. Aquí solo se limpia lo que
    // hubiera dejado escrito la versión anterior.
    const sc = this._scroller;
    if (sc && sc.style.touchAction) sc.style.touchAction = '';
  }

  get _panel() {
    return this.querySelector('[role="dialog"]');
  }

  abrir(origen) {
    super.abrir(origen);
    requestAnimationFrame(() => this._sincronizaGesto());
  }

  _onDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // La cesta es cajón lateral en PC: el arrastre solo tiene sentido como hoja (móvil).
    if (this.hasAttribute('data-bubu-arrastre-movil') && window.innerWidth >= 820) return;
    // Regla 1: el arrastre no se inicia si el toque nace en un control. En «Monta tu
    // caja» esa regla dejaba el gesto muerto: casi toda la hoja son botones y enlaces
    // (los huecos, los extras, el botón de pagar), así que el dedo nunca arrastraba.
    // Ahí sí se arrastra desde encima de un botón: como el puntero solo se captura a
    // los 6 px, un toque limpio sigue siendo un clic normal.
    const esCaja = this._arrastreLibre;
    const control = e.target.closest('button,a,input,select,textarea,[data-bubu-no-arrastre]');
    if (control && (!esCaja || control.matches('input,select,textarea,[data-bubu-no-arrastre]'))) return;
    // Zonas que arrastran: son justo las que no hacen scroll. El pie del total entra
    // aquí porque en «Monta tu caja» va pegado abajo dentro del cuerpo, pero no se
    // desplaza: arrastrar desde el botón de pagar es un gesto natural y no le quita
    // nada a la lista.
    const asa = e.target.closest(
      '[data-bubu-tirador],[data-bubu-hoja-cab],.bubu-hoja__cab,.bubu-cesta__pie,.bubu-caja__esc,.bubu-caja__cta,.bubu-cinta-regalo'
    );
    // Desde la lista TAMBIÉN se arrastra, pero solo cuando ya está arriba del todo y el
    // dedo va hacia abajo: es el gesto de cualquier hoja de iOS y es lo que hace que
    // parezca que la hoja sigue al dedo en vez de tener una tira mágica para cerrarla.
    // Mientras la lista tenga sitio para subir, el gesto es suyo y no se le toca.
    let desdeLista = false;
    if (!asa || !this.contains(asa)) {
      const sc = this._scroller;
      if (sc && sc.contains(e.target)) {
        if (!esCaja || sc.scrollTop > 1) return;
        desdeLista = true;
      }
    }
    const panel = this._panel;
    if (!panel) return;
    this._d = { id: e.pointerId, y0: e.clientY, t0: performance.now(), y: e.clientY, t: performance.now(), vy: 0, capturado: false, panel, dy: -1, desdeLista };
    panel.style.transition = 'none';
    panel.style.willChange = 'transform';
    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
    window.addEventListener('pointercancel', this._onUp);
  }

  _onMove(e) {
    const d = this._d;
    if (!d || e.pointerId !== d.id) return;
    const dy = Math.max(0, e.clientY - d.y0);
    // Nacido sobre la lista: si el dedo sube, el gesto era para la lista y el arrastre
    // se retira sin haber movido nada. Si baja, se queda con él a partir de 5 px.
    if (d.desdeLista && !d.capturado) {
      if (e.clientY - d.y0 < -4) {
        this._limpiaArrastre(true);
        return;
      }
      if (e.clientY - d.y0 < 5) return;
    }
    // Regla 2: capturar el puntero solo pasados 6px. Capturarlo en el pointerdown
    // desvía el clic y se come el de la X.
    if (!d.capturado && Math.abs(e.clientY - d.y0) > 6) {
      d.capturado = true;
      this.classList.add('is-arrastrando');
      // Si el gesto nació sobre un botón, al soltar no debe dispararse su clic.
      if (!this._tragaClic) {
        this._tragaClic = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        window.addEventListener('click', this._tragaClic, true);
      }
      try {
        d.panel.setPointerCapture(d.id);
      } catch (_) {
        /* nada */
      }
    }
    if (!d.capturado) return;
    const ahora = performance.now();
    const dt = ahora - d.t;
    if (dt > 0) d.vy = (e.clientY - d.y) / dt;
    d.y = e.clientY;
    d.t = ahora;
    // El dedo movía la hoja a trompicones por dos motivos, los dos ya arreglados:
    // el desenfoque del fondo se recalculaba en cada fotograma (ahora se apaga
    // mientras dura el gesto, clase is-arrastrando) y el contenedor de dentro
    // reescribía su touch-action en cada scroll. Aquí basta con mover el panel:
    // el navegador ya agrupa los pointermove en uno por fotograma.
    if (d.dy !== dy) {
      d.dy = dy;
      d.panel.style.transform = `translateY(${dy}px)`;
    }
    e.preventDefault();
  }

  /**
   * Con el arrastre ya decidido, el navegador no puede además desplazar la lista.
   *
   * Y ANTES de decidirlo, tampoco. Aquí estaba lo de que «Monta tu caja» no seguía al
   * dedo: el gesto nace sobre la lista, y hasta que el arrastre se daba por bueno (unos
   * milímetros de recorrido) el navegador seguía siendo el dueño. Con la lista ya arriba
   * del todo, tirar hacia abajo es «rebote» para el navegador: en iOS eso dispara la
   * goma elástica, y al dispararla MATA el puntero (pointercancel). El arrastre moría
   * antes de empezar y la hoja se quedaba quieta.
   *
   * La regla es la misma que usa cualquier hoja nativa: si la lista está arriba del todo
   * y el dedo BAJA, ese gesto ya no es del navegador. Se le quita en el primer
   * touchmove, que es el único momento en que todavía se puede.
   */
  _onTouchMove(e) {
    const d = this._d;
    if (!d || !e.cancelable) return;
    if (d.capturado) {
      e.preventDefault();
      return;
    }
    if (!d.desdeLista || !e.touches || e.touches.length !== 1) return;
    const sc = this._scroller;
    if (e.touches[0].clientY - d.y0 > 0 && (!sc || sc.scrollTop <= 0)) e.preventDefault();
  }

  _onUp(e) {
    const d = this._d;
    if (!d || e.pointerId !== d.id) return;
    this._limpiaArrastre(true);
    if (!d.capturado) return;
    const dy = Math.max(0, e.clientY - d.y0);
    if (dy > 96 || d.vy > 0.5) {
      d.panel.style.transition = 'transform .22s ease-in';
      d.panel.style.transform = 'translateY(110%)';
      this.cerrar();
    } else {
      d.panel.style.transition = 'transform var(--bubu-t-vuelve) var(--bubu-ease-vuelve)';
      d.panel.style.transform = '';
    }
  }

  _limpiaArrastre(soloEscuchas) {
    this.classList.remove('is-arrastrando');
    if (this._tragaClic) {
      const f = this._tragaClic;
      this._tragaClic = null;
      setTimeout(() => window.removeEventListener('click', f, true), 0);
    }
    const dd = this._d;
    if (dd && dd.panel) dd.panel.style.willChange = '';
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    window.removeEventListener('pointercancel', this._onUp);
    const d = this._d;
    if (d && d.capturado) {
      try {
        d.panel.releasePointerCapture(d.id);
      } catch (_) {
        /* nada */
      }
    }
    if (!soloEscuchas) {
      const p = this._panel;
      if (p) {
        p.style.transition = '';
        p.style.transform = '';
      }
      this._d = null;
    } else {
      // Se limpia la referencia tras usarla en _onUp.
      setTimeout(() => (this._d = null), 0);
    }
  }
}

if (!customElements.get('bubu-capa')) customElements.define('bubu-capa', BubuCapa);
if (!customElements.get('bubu-hoja')) customElements.define('bubu-hoja', BubuHoja);

// Disparadores globales.
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-bubu-abrir]');
  if (!b) return;
  const capa = document.getElementById(b.getAttribute('data-bubu-abrir'));
  if (!capa || typeof capa.abrir !== 'function') return;
  e.preventDefault();
  capa.abrir(b);
});

// Si el viewport pasa al corte de PC con el menú abierto, se cierra solo.
const mq = window.matchMedia('(min-width: 820px)');
mq.addEventListener('change', (ev) => {
  if (ev.matches) document.querySelectorAll('bubu-capa.bubu-menu, #bubu-hoja-caja').forEach((m) => m.cerrar && m.cerrar());
});

window.BubuCapa = BubuCapa;
window.BubuHoja = BubuHoja;
